import { getChromeIdle } from '../../chrome/wrappers';
import {
  now,
  getMsFromSeconds,
  isSameDay,
  getTimeStringFromDuration,
  getDateTime,
  getISOfromDateTime,
  getStartOfDay,
  getDateMs,
  getDurationFromMs,
  getSystemTimezone,
} from '../../lib/dateTime';
import {
  getDeviceAndUserStore,
  getReportingStore,
  getScreenTimeStore,
} from '../../storage/stores';
import { ScreenTime } from '../../types/store/screenTime';
import {
  isSlotCompletedWhileScreenOff,
  isSlotCompletedWhileScreenOn,
  onSlotCompleted,
  onSlotCompletedWhileScreenOff,
  onSlotCompletedWhileScreenOn,
} from './onCompleted';
import {
  getElapsedTimeSinceLastActiveInMillis,
  getElapsedTimeSinceLastActiveUntilMidnight,
  getElapsedTimeSinceSlotStartUntilMidnight,
  getElapsedTimeSlotInMillis,
} from './elapsed';
import { isDelegationStarted } from '../../delegation/delegation';
import { isProtectionEnabled } from '../../protectionStatus';
import { isSafeNetworkDelegated } from '../../safeNetworks/safeNetworksDelegation';
import { Mutex } from 'async-mutex';
import { onConsumedQuotaUpdated, onNewDay } from '../index';
import { DateTime } from '../../lib/dateTime/types/DateTime';

// Max. frequency for syncing the Screen Time
const syncMaxFrequencyMs = 1000;
let lastSync = -1;

// The system is considered idle if detectionIntervalInSeconds seconds have elapsed since the last user input detected.
// The minimum allowed value is 15 seconds.
// The value it's not very relevant for this code, as we just differentiate with not being locked.
const detectionIntervalInSeconds = 15;

let screenTime: ScreenTime;

/**
 * Returns if it should sync the Screen Time.
 *
 * @remarks
 * There is a maximum of 1 sync per second to prevent any inconsistencies,
 * because checking the screen time too frequently could lead to performance issues, and it's really not necessary.
 */
export const shouldSyncScreenTime = () =>
  isProtectionEnabled() &&
  isDelegationStarted() &&
  !isSafeNetworkDelegated() &&
  didMinimumTimePassSinceLastSync();

const didMinimumTimePassSinceLastSync = () =>
  now() >= lastSync + syncMaxFrequencyMs;

// Since we handle a queue of events on the reports, the execution needs to be synchronized.
const countingMutex = new Mutex();

/**
 * Will update the screen time usage state based on the current idle state of the device (locked or unlocked),
 * also taking into account if the device went into sleep mode,
 * and increment or report a period of usage when needed.
 */
export const syncScreenTime = () => {
  lastSync = now();
  getChromeIdle().queryState(detectionIntervalInSeconds, newState => {
    countingMutex.runExclusive(async () => {
      // Get last saved screen time; otherwise set the default one.
      screenTime = getScreenTimeStore().getScreenTime();
      const currentTime = now();

      if (newState !== 'locked') {
        onScreenOn(currentTime);
      } else {
        onScreenOff(currentTime);
      }

      // Mark the last time the screen time was synced.
      screenTime.lastSyncTimestamp = currentTime;

      // Persist data after sync screen time usage
      await getScreenTimeStore().setScreenTime(screenTime);

      return onConsumedQuotaUpdated();
    });
  });
};

/**
 * Triggered after a screen time sync when the device is active (and not locked)
 */
const onScreenOn = (currentTime: number) => {
  // Last active timestamp will be -1 if it was never set
  const lastActiveTimestamp = screenTime.lastActiveTimestamp;
  screenTime.lastActiveTimestamp =
    lastActiveTimestamp !== -1 ? lastActiveTimestamp : currentTime;

  // Timestamp slot will be -1 if previously was screen off
  const slotStartTimestamp = screenTime.slotStartTimestamp;
  screenTime.slotStartTimestamp =
    slotStartTimestamp !== -1 ? slotStartTimestamp : currentTime;

  // check if new day, if so reset data
  const currentDay = getDateTime(currentTime);
  const didDayChange = didDayChangeSinceLastSync(currentDay);
  if (didDayChange) {
    onDayChanged(currentTime);
  }

  const consumedProfileQuotaInMillis = getMsFromSeconds(
    getDeviceAndUserStore().get()?.consumedQuotaSeconds ?? 0
  );

  const shouldUpdate = shouldUpdateConsumedProfile(
    currentTime,
    consumedProfileQuotaInMillis,
    didDayChange
  );

  if (shouldUpdate) {
    const previousTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    screenTime.totalConsumedInMillis = consumedProfileQuotaInMillis;
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Updated quota from consumed profile: ${previousTotal} -> ${newTotal}`
    );
  }

  const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
    currentTime,
    screenTime.slotStartTimestamp
  );
  const elapsedTimeSinceLastActiveInMillis =
    getElapsedTimeSinceLastActiveInMillis(
      currentTime,
      screenTime.lastActiveTimestamp
    );

  const isSlotCompleted = isSlotCompletedWhileScreenOn(elapsedTimeSlotInMillis);
  const elapsedSlot = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSlotInMillis)
  );
  const elapsedActive = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSinceLastActiveInMillis)
  );

  if (isSlotCompleted) {
    screenTime = onSlotCompletedWhileScreenOn(
      screenTime,
      elapsedTimeSlotInMillis,
      elapsedTimeSinceLastActiveInMillis,
      currentTime
    );
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Slot completed (ON): elapsed=${elapsedSlot}, active=${elapsedActive} -> total=${newTotal}, currentTime=${currentTime}, systemTimezone=${getSystemTimezone()}`
    );
  } else {
    // Period didn't finish yet.
    // Save elapsed time for period
    screenTime.elapsedTimeSlotInMillis = elapsedTimeSlotInMillis;

    // Increment total time, and keep same interval start.
    screenTime.totalConsumedInMillis += elapsedTimeSinceLastActiveInMillis;
  }

  // Chromebook is active, update the last active timestamp to now.
  screenTime.lastActiveTimestamp = currentTime;
};

/**
 * Triggered after a screen time sync when the device is locked
 */
const onScreenOff = (currentTime: number) => {
  const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
    currentTime,
    screenTime.slotStartTimestamp
  );

  if (
    isSlotCompletedWhileScreenOff(
      elapsedTimeSlotInMillis,
      screenTime.slotStartTimestamp
    )
  ) {
    const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
      currentTime,
      screenTime.slotStartTimestamp
    );
    const elapsedTimeSinceLastActiveInMillis =
      getElapsedTimeSinceLastActiveInMillis(
        currentTime,
        screenTime.lastActiveTimestamp
      );

    screenTime = onSlotCompletedWhileScreenOff(
      screenTime,
      elapsedTimeSlotInMillis,
      elapsedTimeSinceLastActiveInMillis
    );

    const elapsedSlot = getTimeStringFromDuration(
      getDurationFromMs(elapsedTimeSlotInMillis)
    );
    const elapsedActive = getTimeStringFromDuration(
      getDurationFromMs(elapsedTimeSinceLastActiveInMillis)
    );
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Slot completed (OFF): elapsed=${elapsedSlot}, active=${elapsedActive} -> total=${newTotal}, currentTime=${currentTime}, systemTimezone=${getSystemTimezone()}`
    );
  }
};

/**
 * Check if should update consumed usage from profile.
 *
 * @remarks
 * If the day just changed, we don't want to overwrite the screen time with the backend consumed quota immediatelly
 * to avoid inconsistencies due to race conditions (CHR-343).
 */
const shouldUpdateConsumedProfile = (
  currentTime: number,
  consumedProfileQuotaInMillis: number,
  didDayChange: boolean
) => {
  const consumedQuotaTimestamp =
    getReportingStore().getTimestamp() ?? currentTime;
  const isQuotaFromSameDay = isSameDay(
    getDateTime(currentTime),
    getDateTime(consumedQuotaTimestamp)
  );
  const consumedQuotaIsGreaterThanScreenTime =
    consumedProfileQuotaInMillis > screenTime.totalConsumedInMillis;

  const shouldUpdate =
    isQuotaFromSameDay && consumedQuotaIsGreaterThanScreenTime && !didDayChange;
  if (shouldUpdate) {
    console.log(
      `[shouldUpdateConsumedProfile] true - currentTime=${currentTime}, systemTimezone=${getSystemTimezone()}, consumedQuotaTimestamp=${consumedQuotaTimestamp}, isQuotaFromSameDay=${isQuotaFromSameDay}, consumedProfileQuotaInMillis=${consumedProfileQuotaInMillis}, screenTimeTotalConsumedInMillis=${
        screenTime.totalConsumedInMillis
      }, didDayChange=${didDayChange}`
    );
  }

  return shouldUpdate;
};

const didDayChangeSinceLastSync = (currentDay: DateTime) => {
  // Fallback to use lastActiveTimestamp as the last sync timestamp
  // for devices that don't have lastSyncTimestamp yet (migration)
  // TODO: Remove this fallback once all devices have version 0.20 or higher
  if (screenTime.lastSyncTimestamp === undefined) {
    const lastActiveDay = getDateTime(screenTime.lastActiveTimestamp);
    return !isSameDay(currentDay, lastActiveDay);
  }

  // Skip for the first ever sync
  if (screenTime.lastSyncTimestamp === -1) {
    return false;
  }

  // Day changed if the current day is not the same as the last screen time sync day
  const lastSyncDay = getDateTime(screenTime.lastSyncTimestamp);
  return !isSameDay(currentDay, lastSyncDay);
};

const onDayChanged = (currentTime: number) => {
  const totalConsumedInMillis = getTimeStringFromDuration(
    getDurationFromMs(screenTime.totalConsumedInMillis)
  );

  console.info(`[ScreenTime] Day Changed! 
    totalConsumedInMillis=${totalConsumedInMillis},
    lastSyncTimestamp=${screenTime.lastSyncTimestamp},
    lastActiveTimestamp=${screenTime.lastActiveTimestamp},
    currentTime=${currentTime}, 
    systemTimezone=${getSystemTimezone()}`);

  // Time until midnight.
  const elapsedTimeSinceSlotStartUntilMidnight =
    getElapsedTimeSinceSlotStartUntilMidnight(
      currentTime,
      screenTime.slotStartTimestamp
    );
  const elapsedTimeSinceLastActiveUntilMidnight =
    getElapsedTimeSinceLastActiveUntilMidnight(
      currentTime,
      screenTime.lastActiveTimestamp
    );

  const slotUntilMidnight = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSinceSlotStartUntilMidnight)
  );
  const activeUntilMidnight = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSinceLastActiveUntilMidnight)
  );

  screenTime = onSlotCompleted(
    screenTime,
    elapsedTimeSinceSlotStartUntilMidnight,
    elapsedTimeSinceLastActiveUntilMidnight
  );

  // Reset data because new day
  const startDayTimestampInMillis = getDateMs(
    getStartOfDay(getDateTime(currentTime))
  );

  screenTime.totalConsumedInMillis = 0;
  screenTime.slotStartTimestamp = startDayTimestampInMillis;
  screenTime.elapsedTimeSlotInMillis = 0;
  screenTime.lastActiveTimestamp = startDayTimestampInMillis;

  console.log(
    `[ScreenTime] Day reset complete: slot=${slotUntilMidnight}, active=${activeUntilMidnight}, new start: ${getISOfromDateTime(
      getDateTime(startDayTimestampInMillis)
    )}, currentTime=${currentTime}, systemTimezone=${getSystemTimezone()}`
  );

  onNewDay();
};
